import { Tabs, usePathname, useRouter } from "expo-router";
import { Image, Pressable, StyleSheet, Text, View, useWindowDimensions } from "react-native";

import { AccountBar } from "../../src/account-bar";
import { colour, radius, space, type } from "../../src/theme";

/**
 * The three places you can be (WI-044), on whatever size screen (WI-049).
 *
 * Tabs across the bottom is a phone convention. On a desktop browser it puts
 * the primary navigation as far from the content as the viewport allows, and
 * the app looked like a phone screenshot stretched to 2000px — which is what
 * "a dog's breakfast of mobile and web views" meant.
 *
 * So above a breakpoint the same three destinations move to a row at the top,
 * the bottom bar is hidden, and the content stops growing at a readable width.
 * One set of routes, two arrangements; no second navigation to keep in step.
 */

/** Where a phone layout stops being the right one. Not a device — a width. */
export const WIDE = 900;

/**
 * Which chrome a width gets.
 *
 * Pulled out as a function because a component test cannot see it otherwise:
 * `Tabs` has to be mocked, and a mock does not apply `screenOptions`, so
 * "the bottom bar is hidden on a wide screen" was asserted nowhere. A seed
 * proved that by not failing.
 */
export function chromeFor(width: number): { topNav: boolean; bottomBar: boolean } {
  const wide = width >= WIDE;
  // Never both: two navigations for three destinations is worse than either.
  return { topNav: wide, bottomBar: !wide };
}

const DESTINATIONS = [
  { href: "/", label: "Search" },
  { href: "/shelf", label: "Shelf" },
  { href: "/plays", label: "Plays" },
] as const;

/**
 * The same mark the site wears (WI-086).
 *
 * The app had no branding at all -- the mark reached it as a favicon and
 * nowhere else -- so arriving from the site's header meant the logo and nav
 * vanishing into a line of grey text. Same square, same 36px, same header
 * shell, so the two read as one product.
 *
 * From `public/`, not drawn in Views: the mark is full of hex and
 * check-design-tokens.mjs scans `src`, which is why WI-085 put the marks in
 * `public/` in the first place. `Image` with a uri is how cover.tsx already
 * loads one, and it costs no dependency against a bundle with 48KB spare.
 */
function Brand() {
  const router = useRouter();

  return (
    <Pressable
      onPress={() => router.navigate("/")}
      accessibilityRole="link"
      aria-label="Shake Pass Play, home"
      style={styles.brand}
    >
      <Image source={{ uri: "/icon.svg" }} style={styles.brandMark} accessibilityLabel="" />
    </Pressable>
  );
}

function TopNav() {
  const pathname = usePathname();
  const router = useRouter();

  return (
    <View style={styles.topNav}>
      {DESTINATIONS.map((d) => {
        const active = pathname === d.href;
        return (
          <Pressable
            key={d.href}
            onPress={() => router.navigate(d.href)}
            accessibilityRole="link"
            // `aria-current`, not `aria-selected`: these are links, and
            // `selected` is only valid on roles like tab and option -- so
            // react-native-web drops it and a screen reader is told nothing
            // about which page you are on.
            aria-current={active ? "page" : undefined}
            style={[styles.navItem, active && styles.navItemActive]}
          >
            <Text style={[styles.navLabel, active && styles.navLabelActive]}>{d.label}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}

export default function TabsLayout() {
  const { width } = useWindowDimensions();
  const { topNav, bottomBar } = chromeFor(width);

  return (
    <View style={styles.shell}>
      <View style={styles.chrome}>
        <View style={styles.centred}>
          {/*
           * Mark left, account right, on one row -- the site's arrangement.
           * The nav sits under it on a wide screen and moves to the bottom
           * bar on a narrow one, which is the app's own business: the site
           * has two destinations and this has three.
           */}
          <View style={styles.headRow}>
            <Brand />
            <View style={styles.account}>
              <AccountBar />
            </View>
          </View>
          {topNav && <TopNav />}
        </View>
      </View>

      <Tabs
        screenOptions={{
          // The tab labels are the titles; a header as well would be two rows
          // of chrome saying the same word.
          headerShown: false,
          tabBarActiveTintColor: colour.accent,
          tabBarInactiveTintColor: colour.inkFaint,
          // Hidden rather than unmounted: the routes stay identical, so
          // resizing the window does not remount a screen mid-edit.
          tabBarStyle: bottomBar ? styles.bar : styles.hidden,
          tabBarLabelStyle: styles.tabLabel,
          // There is no icon set (the bundle had 48KB spare), so the label is
          // the whole control and gets the height an icon would have taken.
          tabBarIconStyle: styles.hidden,
        }}
      >
        <Tabs.Screen name="index" options={{ title: "Search" }} />
        <Tabs.Screen name="shelf" options={{ title: "Shelf" }} />
        <Tabs.Screen name="plays" options={{ title: "Plays" }} />
      </Tabs>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: { flex: 1, backgroundColor: colour.canvas },
  chrome: {
    backgroundColor: colour.surface,
    borderBottomWidth: 1,
    borderBottomColor: colour.line,
    paddingHorizontal: space.lg,
    paddingTop: space.md,
  },
  /**
   * Content stops growing at a readable width; chrome stays full-bleed.
   *
   * 1024 rather than 960 to match the site's `64rem` inner width, so the mark
   * lands in the same place on both and moving between them does not shift it.
   */
  centred: { width: "100%", maxWidth: 1024, alignSelf: "center" },

  /** Mark and account state, the row the site puts its mark and search in. */
  headRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: space.lg,
    paddingBottom: space.md,
  },
  brand: { flexShrink: 0 },
  /** The site's `.brand__mark` is 36px. */
  brandMark: { width: 36, height: 36 },
  /** Takes the rest of the row, so the account state ends at the right edge. */
  account: { flex: 1, minWidth: 0 },

  topNav: { flexDirection: "row", gap: space.xs, paddingBottom: space.sm },
  navItem: {
    paddingVertical: space.sm,
    paddingHorizontal: space.md,
    borderRadius: radius.md,
  },
  navItemActive: { backgroundColor: colour.sunken },
  navLabel: { ...type.meta, fontWeight: "600", color: colour.inkMuted },
  navLabelActive: { color: colour.accent },

  bar: {
    backgroundColor: colour.surface,
    borderTopColor: colour.line,
    borderTopWidth: 1,
    height: 58,
    paddingTop: space.sm,
  },
  tabLabel: { ...type.meta, fontWeight: "600" },
  hidden: { display: "none" },
});
